import { useEffect, useRef, useState } from 'react';

/**
 * Magic Pool Ball overlay widget.
 *
 * Subscribes to `magic-pool-ball-trigger` events fan-outed by ws-gateway.
 * On each event: queue a roll-in → hold → roll-out animation. While an
 * animation is already playing, new events queue (cap at 4 to avoid stale
 * backlog after chat bursts).
 *
 * 1920x1080 design region scaled to fit any OBS source size via CSS
 * `transform: scale(min(100vw/1920, 100vh/1080))` on an inner stage.
 */

export interface MagicPoolBallEvent {
  type: 'magic_pool_ball';
  ball: number;
  ballType: 'solid' | 'stripe' | 'eight' | 'cue';
  ballColor: string;
  response: string;
  viewer: string;
  question: string;
  ts: number;
}

interface Fields {
  showQuestion?: boolean;
  position?: 'bottom-center' | 'center' | 'top-center';
  displayDurationMs?: number;
}

interface Props {
  streamerId: string;
  event: MagicPoolBallEvent | null;
  fields: Fields;
}

const ROLL_IN_MS = 700;
const ROLL_OUT_MS = 700;
const DEFAULT_HOLD_MS = 4000;
const QUEUE_CAP = 4;

const ANIMATIONS = `
@keyframes mpb-rollInLeft {
  0%   { transform: translateX(-120vw) rotate(-720deg); opacity: 0; }
  80%  { transform: translateX(20px) rotate(-10deg); opacity: 1; }
  100% { transform: translateX(0) rotate(0); opacity: 1; }
}
@keyframes mpb-rollInRight {
  0%   { transform: translateX(120vw) rotate(720deg); opacity: 0; }
  80%  { transform: translateX(-20px) rotate(10deg); opacity: 1; }
  100% { transform: translateX(0) rotate(0); opacity: 1; }
}
@keyframes mpb-rollOutLeft {
  0%   { transform: translateX(0) rotate(0); opacity: 1; }
  100% { transform: translateX(-120vw) rotate(-720deg); opacity: 0; }
}
@keyframes mpb-rollOutRight {
  0%   { transform: translateX(0) rotate(0); opacity: 1; }
  100% { transform: translateX(120vw) rotate(720deg); opacity: 0; }
}
@keyframes mpb-textIn {
  0%   { transform: translateY(20px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}
@keyframes mpb-textOut {
  0%   { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(8px); opacity: 0; }
}
`;

export function MagicPoolBallWidget({ streamerId, event, fields }: Props) {
  const showQuestion = fields.showQuestion !== false;
  const position = fields.position ?? 'bottom-center';
  const holdMs =
    typeof fields.displayDurationMs === 'number'
      ? Math.max(1000, Math.min(15000, fields.displayDurationMs))
      : DEFAULT_HOLD_MS;

  // Queue + active-playback state. The widget only renders one ball at a
  // time; queued events play back-to-back.
  const queueRef = useRef<MagicPoolBallEvent[]>([]);
  const playingRef = useRef(false);
  const [active, setActive] = useState<MagicPoolBallEvent | null>(null);
  const [phase, setPhase] = useState<'rollIn' | 'hold' | 'rollOut' | 'idle'>('idle');
  const [direction, setDirection] = useState<'left' | 'right'>('left');
  void streamerId;

  useEffect(() => {
    if (!event) return;
    // Dedup by ts: socket replay or React strict-mode could re-fire.
    if (queueRef.current.some((q) => q.ts === event.ts)) return;
    queueRef.current.push(event);
    if (queueRef.current.length > QUEUE_CAP) {
      queueRef.current.splice(0, queueRef.current.length - QUEUE_CAP);
    }
    pump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  function pump() {
    if (playingRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    playingRef.current = true;
    setDirection(Math.random() < 0.5 ? 'left' : 'right');
    setActive(next);
    setPhase('rollIn');

    const tHold = setTimeout(() => setPhase('hold'), ROLL_IN_MS);
    const tOut = setTimeout(() => setPhase('rollOut'), ROLL_IN_MS + holdMs);
    const tDone = setTimeout(
      () => {
        setPhase('idle');
        setActive(null);
        playingRef.current = false;
        pump();
      },
      ROLL_IN_MS + holdMs + ROLL_OUT_MS,
    );

    // Stash timer IDs on the ref so unmount cancels them.
    timersRef.current.push(tHold, tOut, tDone);
  }

  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => {
    return () => {
      for (const t of timersRef.current) clearTimeout(t);
    };
  }, []);

  if (!active || phase === 'idle') {
    return <style>{ANIMATIONS}</style>;
  }

  const animation =
    phase === 'rollIn'
      ? `mpb-${direction === 'left' ? 'rollInLeft' : 'rollInRight'} ${ROLL_IN_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`
      : phase === 'rollOut'
        ? `mpb-${direction === 'left' ? 'rollOutRight' : 'rollOutLeft'} ${ROLL_OUT_MS}ms cubic-bezier(0.32, 0, 0.67, 0) forwards`
        : undefined;

  const textAnimation =
    phase === 'rollOut' ? `mpb-textOut 250ms ease forwards` : `mpb-textIn 350ms 150ms ease both`;

  // Inner stage: 1920x1080, scaled to fit viewport. All children use pixel
  // sizing within this fixed design region.
  const fitScale = `min(calc(100vw / 1920), calc(100vh / 1080))`;

  // Anchor positions inside the 1920x1080 stage.
  const anchorStyle: React.CSSProperties =
    position === 'center'
      ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
      : position === 'top-center'
        ? { top: 140, left: '50%', transform: 'translateX(-50%)' }
        : { bottom: 120, left: '50%', transform: 'translateX(-50%)' };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <style>{ANIMATIONS}</style>
      <div
        style={{
          position: 'relative',
          width: 1920,
          height: 1080,
          flexShrink: 0,
          transform: `scale(${fitScale})`,
          transformOrigin: 'center center',
        }}
      >
        <div
          style={{
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 24,
            ...anchorStyle,
          }}
        >
          <div style={{ animation }}>
            <PoolBallSVG ball={active.ball} ballType={active.ballType} color={active.ballColor} />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              animation: textAnimation,
            }}
          >
            <div
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 56,
                fontWeight: 900,
                letterSpacing: -0.5,
                color: '#fff',
                textShadow:
                  '0 4px 24px rgba(0,0,0,0.7), 0 0 40px rgba(0,0,0,0.5), 0 1px 0 rgba(0,0,0,0.6)',
                textAlign: 'center',
                padding: '8px 24px',
                maxWidth: 1200,
              }}
            >
              {active.response}
            </div>
            <div
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 22,
                fontWeight: 600,
                color: 'rgba(255,255,255,0.85)',
                textShadow: '0 2px 12px rgba(0,0,0,0.7)',
                textAlign: 'center',
              }}
            >
              <span style={{ color: '#f655a6' }}>{active.viewer}</span>{' '}
              {showQuestion && active.question
                ? `asked: "${active.question}"`
                : 'asked the magic pool ball'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** SVG pool ball — 240px design size. Solid: full color + white number circle.
 *  Stripe: white with a horizontal color band. Cue: plain white. 8: black. */
function PoolBallSVG({
  ball,
  ballType,
  color,
}: {
  ball: number;
  ballType: 'solid' | 'stripe' | 'eight' | 'cue';
  color: string;
}) {
  const size = 280;
  const r = size / 2;
  const numberLabel = ballType === 'cue' ? '' : String(ball);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ filter: 'drop-shadow(0 24px 32px rgba(0,0,0,0.6))' }}
    >
      <defs>
        <radialGradient id={`mpb-shade-${ball}`} cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
          <stop offset="20%" stopColor="rgba(255,255,255,0.15)" />
          <stop offset="65%" stopColor="rgba(0,0,0,0)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.55)" />
        </radialGradient>
      </defs>
      {/* Base */}
      {ballType === 'stripe' ? (
        <>
          <circle cx={r} cy={r} r={r - 2} fill="#fff" />
          {/* Color stripe — horizontal band ~36% of diameter. */}
          <rect x={0} y={r - size * 0.22} width={size} height={size * 0.44} fill={color} />
          {/* Mask the stripe back to circle */}
          <circle cx={r} cy={r} r={r - 2} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth={2} />
        </>
      ) : (
        <circle cx={r} cy={r} r={r - 2} fill={color} />
      )}
      {/* 3D shading overlay */}
      <circle cx={r} cy={r} r={r - 2} fill={`url(#mpb-shade-${ball})`} />
      {/* Specular highlight */}
      <ellipse
        cx={r - r * 0.35}
        cy={r - r * 0.42}
        rx={r * 0.28}
        ry={r * 0.18}
        fill="rgba(255,255,255,0.55)"
      />
      {/* Number circle */}
      {numberLabel && (
        <>
          <circle cx={r} cy={r} r={r * 0.32} fill="#fff" />
          <text
            x={r}
            y={r}
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily="Inter, system-ui, sans-serif"
            fontWeight={800}
            fontSize={r * 0.4}
            fill={ballType === 'eight' ? '#0a0a0a' : '#0a0a0a'}
          >
            {numberLabel}
          </text>
        </>
      )}
    </svg>
  );
}
